import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { del, get, post } from "../../services/api";

/* ============================================================
   DEFAULT TAB SETTINGS (Single Source of Truth)
============================================================ */
const DEFAULT_TAB_SETTINGS = {
  "my-drawings": {
    defaultView: "list",
    showThumbnails: true,
    showFileSize: true,
    showModifiedDate: true,
    defaultSortBy: "name",
    enableFavorites: true,
  },
  "site-recordings": {
    defaultView: "list",
    showThumbnails: true,
    showDuration: true,
    showLocation: true,
    defaultSortBy: "timestamp",
  },
  "rec-video": {
    defaultVolume: 70,
    enableAutoPlay: false,
    showControls: true,
    loopVideo: false,
  },
};

/* ============================================================
   HELPERS
============================================================ */
const mergeWithDefaults = (tabKey, apiSettings = {}) => ({
  ...DEFAULT_TAB_SETTINGS[tabKey],
  ...apiSettings,
});

/* ============================================================
   INITIAL STATE
============================================================ */
const initialState = {
  user: null,
  displayTabSettings: structuredClone(DEFAULT_TAB_SETTINGS),
};

/* ============================================================
   THUNKS
============================================================ */

/** Fetch display settings (GET) */
export const fetchUserDisplaySettings = createAsyncThunk(
  "user/fetchUserDisplaySettings",
  async (tabKey, { rejectWithValue }) => {
    try {
      const response = await get(`/user-display-settings/${tabKey}`);
      if (response?.success) {
        return { tabKey, settings: response.data || {} };
      }
      throw new Error("Invalid fetch response");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Save display settings (POST) */
export const saveUserDisplaySettings = createAsyncThunk(
  "user/saveUserDisplaySettings",
  async ({ tabKey, settings }, { rejectWithValue }) => {
    try {
      const response = await post(`/user-display-settings/${tabKey}`, settings);
      if (response?.success) {
        return { tabKey, settings: response.data || {} };
      }
      throw new Error("Invalid save response");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/** Reset display settings (DELETE) */
export const resetUserDisplaySettings = createAsyncThunk(
  "user/resetUserDisplaySettings",
  async ({ tabKey }, { rejectWithValue }) => {
    try {
      const response = await del(`/user-display-settings/${tabKey}/reset`);
      if (response?.success) {
        return { tabKey, settings: response.data || {} };
      }
      throw new Error("Invalid reset response");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

/* ============================================================
   SLICE
============================================================ */
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },

    /** Immediate local update (Apply button) */
    setDisplayTabSettings: (state, action) => {
      const { tabKey, settings } = action.payload;
      if (DEFAULT_TAB_SETTINGS[tabKey]) {
        state.displayTabSettings[tabKey] = mergeWithDefaults(tabKey, settings);
      }
    },

    /** Local reset without API */
    resetDisplayTabSettingsLocal: (state, action) => {
      const tabKey = action.payload;
      if (DEFAULT_TAB_SETTINGS[tabKey]) {
        state.displayTabSettings[tabKey] = structuredClone(
          DEFAULT_TAB_SETTINGS[tabKey]
        );
      }
    },
  },

  /* ============================================================
     EXTRA REDUCERS
  ============================================================ */
  extraReducers: (builder) => {
    builder
      /* ---------- FETCH ---------- */
      .addCase(fetchUserDisplaySettings.fulfilled, (state, action) => {
        const { tabKey, settings } = action.payload;
        if (DEFAULT_TAB_SETTINGS[tabKey]) {
          state.displayTabSettings[tabKey] = mergeWithDefaults(
            tabKey,
            settings
          );
        }
      })
      .addCase(fetchUserDisplaySettings.rejected, (state, action) => {
        const tabKey = action.meta.arg;
        if (DEFAULT_TAB_SETTINGS[tabKey]) {
          state.displayTabSettings[tabKey] = structuredClone(
            DEFAULT_TAB_SETTINGS[tabKey]
          );
        }
      })

      /* ---------- SAVE ---------- */
      .addCase(saveUserDisplaySettings.fulfilled, (state, action) => {
        const { tabKey, settings } = action.payload;
        if (DEFAULT_TAB_SETTINGS[tabKey]) {
          state.displayTabSettings[tabKey] = mergeWithDefaults(
            tabKey,
            settings
          );
        }
      })

      /* ---------- RESET ---------- */
      .addCase(resetUserDisplaySettings.fulfilled, (state, action) => {
        const { tabKey, settings } = action.payload;
        if (DEFAULT_TAB_SETTINGS[tabKey]) {
          state.displayTabSettings[tabKey] = mergeWithDefaults(
            tabKey,
            settings
          );
        }
      })
      .addCase(resetUserDisplaySettings.rejected, (state, action) => {
        const tabKey = action.meta.arg?.tabKey;
        if (DEFAULT_TAB_SETTINGS[tabKey]) {
          state.displayTabSettings[tabKey] = structuredClone(
            DEFAULT_TAB_SETTINGS[tabKey]
          );
        }
      });
  },
});

/* ============================================================
   EXPORTS
============================================================ */
export const { setUser, setDisplayTabSettings, resetDisplayTabSettingsLocal } =
  userSlice.actions;

export default userSlice.reducer;
